import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import {
  ProcessMovementDto,
  PublicationFilterDto,
  PublicationCountsDto,
  PaginatedPublicationsResponse,
} from '@prometeus/contracts';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { useWorkspaceStore } from '../stores/workspace.store';
import {
  TreatPublicationModal,
  type PendingTreatPublicationRow,
} from '../components/TreatPublicationModal';
import { Breadcrumb } from '../components/Breadcrumb';
import { ClientFilter } from '../components/Agenda/ClientFilter';
import { DateRangeFilter } from '../components/Agenda/DateRangeFilter';
import { PublicationStatusFilter, type PublicationStatusValue } from '../components/Publications/PublicationStatusFilter';
import { PublicationOrigemFilter, type PublicationOrigemValue } from '../components/Publications/PublicationOrigemFilter';
import { PublicationTribunalFilter } from '../components/Publications/PublicationTribunalFilter';
import { PublicationCard } from '../components/Publications/PublicationCard';
import { maybeMarkPublicationAsRead } from '../components/Publications/maybeMarkPublicationAsRead';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useDebounce } from '../hooks/useDebounce';
import { useEventBusStore } from '../stores/event-bus.store';

export const Route = createFileRoute('/w/$workspaceId/publications')({
  component: PublicationsPage,
});

function PublicationsPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const { confirm: confirmDialog } = useConfirmDialog();
  const addToast = useEventBusStore((s) => s.addToast);
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const setWorkspace = useWorkspaceStore((s) => s.setWorkspace);
  const [statusFilter, setStatusFilter] = useState<PublicationStatusValue>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [clientFilterName, setClientFilterName] = useState<string>('');
  const [page, setPage] = useState(0);
  const [selectedPublication, setSelectedPublication] = useState<ProcessMovementDto | null>(null);
  const [treatDraftByPublicationId, setTreatDraftByPublicationId] = useState<
    Record<string, PendingTreatPublicationRow[]>
  >({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [tribunalFilter, setTribunalFilter] = useState('');
  const [origemFilter, setOrigemFilter] = useState<PublicationOrigemValue>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const limit = 20;

  const handleTreatPendingRowsChange = useCallback(
    (publicationId: string, rows: PendingTreatPublicationRow[]) => {
      setTreatDraftByPublicationId((prev) => ({ ...prev, [publicationId]: rows }));
    },
    [],
  );

  const handleClearTreatPublicationDraft = useCallback((publicationId: string) => {
    setTreatDraftByPublicationId((prev) => {
      if (!(publicationId in prev)) return prev;
      const next = { ...prev };
      delete next[publicationId];
      return next;
    });
  }, []);

  const canRead = hasPermission('publications.read');

  useEffect(() => {
    if (!workspaceId) return;
    if (!currentWorkspace || currentWorkspace.id !== workspaceId) {
      setWorkspace({ id: workspaceId, name: '', slug: '' });
    }
  }, [workspaceId, currentWorkspace?.id, setWorkspace]);

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!canRead) {
      showPermissionDenied('Ver publicações');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    }
  }, [location.pathname, canRead, showPermissionDenied, navigate, workspaceId]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const goToToday = () => {
    const today = formatDateForAPI(new Date());
    setDateFrom(today);
    setDateTo(today);
    setPage(0);
  };

  const handleDateFilterChange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    setPage(0);
  };

  const handleClearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const isTodayRange =
    Boolean(dateFrom && dateTo) &&
    dateFrom === dateTo &&
    dateFrom === formatDateForAPI(new Date());

  const filters: PublicationFilterDto = {
    status: statusFilter || undefined,
    clientId: clientFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    search: debouncedSearch.trim() || undefined,
    tribunalId: tribunalFilter || undefined,
    origem: origemFilter || undefined,
    page,
    limit,
  };

  const { data: publicationsData, isLoading } = useQuery<PaginatedPublicationsResponse>({
    queryKey: ['publications', workspaceId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.search) params.append('search', filters.search);
      if (filters.tribunalId) params.append('tribunalId', filters.tribunalId);
      if (filters.origem) params.append('origem', filters.origem);
      params.append('page', String(filters.page || 0));
      params.append('limit', String(filters.limit || 20));

      const response = await api.get(`/publications?${params.toString()}`);
      return response.data.data;
    },
    enabled: !!workspaceId && canRead,
  });

  const countsKey = [
    dateFrom || 'all',
    dateTo || 'all',
    clientFilter || 'all',
  ].join('|');
  const { data: publicationCounts, isLoading: isLoadingCounts } =
    useQuery<PublicationCountsDto>({
      queryKey: ['publications-counts', workspaceId, countsKey],
      queryFn: async () => {
        const params = new URLSearchParams();
        if (clientFilter) params.append('clientId', clientFilter);
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);
        const qs = params.toString();
        const response = await api.get(
          `/publications/counts${qs ? `?${qs}` : ''}`,
        );
        return response.data.data ?? response.data;
      },
      enabled: !!workspaceId && canRead,
    });

  const { data: tribunalOptions = [], isLoading: isLoadingTribunais } = useQuery({
    queryKey: ['publications-filter-tribunais', workspaceId],
    queryFn: async () => {
      const response = await api.get('/publications/filter-options/tribunais');
      return (response.data.data ?? response.data) as Array<{
        id: string;
        sigla: string;
        nome: string;
      }>;
    },
    enabled: !!workspaceId && canRead,
  });

  const invalidatePublicationQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['publications', workspaceId] });
    queryClient.invalidateQueries({ queryKey: ['publications-counts', workspaceId] });
    // Sidebar e consumidores legados ainda usam /publications/count
    queryClient.invalidateQueries({ queryKey: ['publications-count', workspaceId] });
  };

  const markAsReadMutation = useMutation({
    mutationFn: async (publicationId: string) => {
      const response = await api.patch(`/publications/${publicationId}/read`);
      return response.data.data;
    },
    onSuccess: invalidatePublicationQueries,
    onError: () => {
      /* best-effort: abrir o modal não depende do mark-as-read */
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (publicationId: string) => {
      const response = await api.post(`/publications/${publicationId}/restore`);
      return response.data.data;
    },
    onSuccess: invalidatePublicationQueries,
  });

  const discardMutation = useMutation({
    mutationFn: async (publicationId: string) => {
      const response = await api.post(`/publications/${publicationId}/discard`);
      return response.data.data;
    },
    onSuccess: (_data, publicationId) => {
      invalidatePublicationQueries();
      handleClearTreatPublicationDraft(publicationId);
      setSelectedIds((prev) => {
        if (!prev.has(publicationId)) return prev;
        const next = new Set(prev);
        next.delete(publicationId);
        return next;
      });
    },
    onError: (error: any) => {
      addToast(
        error.response?.data?.message || 'Não foi possível descartar a publicação.',
        'error',
      );
    },
  });

  const discardBulkMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await api.post('/publications/discard-bulk', { ids });
      return (response.data.data ?? response.data) as {
        discarded: string[];
        skipped: Array<{ id: string; reason: string }>;
      };
    },
    onSuccess: (result) => {
      invalidatePublicationQueries();
      for (const id of result.discarded) {
        handleClearTreatPublicationDraft(id);
      }
      setSelectedIds(new Set());
      if (result.skipped.length > 0) {
        addToast(
          `${result.discarded.length} descartada(s); ${result.skipped.length} ignorada(s).`,
          'warning',
        );
      } else {
        addToast(
          result.discarded.length === 1
            ? 'Publicação descartada.'
            : `${result.discarded.length} publicações descartadas.`,
          'success',
        );
      }
    },
    onError: (error: any) => {
      addToast(
        error.response?.data?.message || 'Não foi possível descartar as publicações.',
        'error',
      );
    },
  });

  const duplicataMutation = useMutation({
    mutationFn: async ({ id, mesma }: { id: string; mesma: boolean }) => {
      const response = await api.post(`/publications/${id}/duplicata`, { mesma });
      return response.data.data;
    },
    onSuccess: invalidatePublicationQueries,
  });

  const handleClientFilterChange = (clientId: string, clientName: string) => {
    setClientFilter(clientId);
    setClientFilterName(clientName);
    setPage(0);
  };

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
    enabled: canRead,
  });

  if (!canRead) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Publicações' },
          ]}
        />

        <div
          className="bg-white dark:bg-gray-800 mt-6 flex flex-col"
          style={{
            borderRadius: 15,
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Header do card */}
          <div
            className="flex flex-col gap-4 flex-shrink-0 p-5 pb-4 dark:border-gray-700"
            style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
          >
            <div>
              <h1
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#06141B',
                }}
              >
                Publicações
              </h1>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#9BA8AB',
                }}
              >
                Trate as atualizações dos processos criando tarefas
              </p>
            </div>

            {/* Período + atalho Hoje */}
            <div className="flex flex-wrap items-center gap-3">
              <DateRangeFilter
                dateFrom={dateFrom}
                dateTo={dateTo}
                onChange={handleDateFilterChange}
                onClear={handleClearDateFilter}
              />
              {!isTodayRange && (
                <button
                  type="button"
                  onClick={goToToday}
                  className="px-3 py-2 rounded-[10px] transition-colors hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    backgroundColor: '#F9FAFB',
                    color: '#2D3748',
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  Hoje
                </button>
              )}
            </div>

            {/* Statistics - mesma linha, cards compactos */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div
                className="p-4 rounded-[10px]"
                style={{
                  backgroundColor: '#FBFBFB',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(6, 20, 27, 0.08)',
                }}
              >
                <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 600, color: '#06141B' }}>
                  Não tratadas
                </h3>
                <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#718096', marginTop: 4 }}>
                  {isLoadingCounts ? '...' : publicationCounts?.unread ?? 0}
                </p>
              </div>
              <div
                className="p-4 rounded-[10px]"
                style={{
                  backgroundColor: '#FBFBFB',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(6, 20, 27, 0.08)',
                }}
              >
                <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 600, color: '#06141B' }}>
                  Lidas
                </h3>
                <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#718096', marginTop: 4 }}>
                  {isLoadingCounts ? '...' : publicationCounts?.read ?? 0}
                </p>
              </div>
              <div
                className="p-4 rounded-[10px]"
                style={{
                  backgroundColor: '#FBFBFB',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(6, 20, 27, 0.08)',
                }}
              >
                <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 600, color: '#06141B' }}>
                  Tratadas
                </h3>
                <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#718096', marginTop: 4 }}>
                  {isLoadingCounts ? '...' : publicationCounts?.treated ?? 0}
                </p>
              </div>
              <div
                className="p-4 rounded-[10px]"
                style={{
                  backgroundColor: '#FBFBFB',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(6, 20, 27, 0.08)',
                }}
              >
                <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 600, color: '#06141B' }}>
                  Descartadas
                </h3>
                <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#718096', marginTop: 4 }}>
                  {isLoadingCounts ? '...' : publicationCounts?.discarded ?? 0}
                </p>
              </div>
            </div>

            {/* Filtros - dropdowns no padrão Agenda */}
            <div className="flex flex-wrap items-center gap-3">
              <PublicationStatusFilter
                value={statusFilter}
                onChange={(v) => {
                  setStatusFilter(v);
                  setPage(0);
                }}
              />
              <ClientFilter
                selectedClientId={clientFilter}
                selectedClientName={clientFilterName}
                onChange={handleClientFilterChange}
              />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar descrição ou processo..."
                className="px-4 py-2 rounded-[10px] border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[200px] max-w-xs"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#2D3748',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 0 15px rgba(0, 0, 0, 0.05)',
                }}
              />
              <PublicationTribunalFilter
                value={tribunalFilter}
                options={tribunalOptions}
                isLoading={isLoadingTribunais}
                onChange={(id) => {
                  setTribunalFilter(id);
                  setPage(0);
                }}
              />
              <PublicationOrigemFilter
                value={origemFilter}
                onChange={(v) => {
                  setOrigemFilter(v);
                  setPage(0);
                }}
              />
            </div>
          </div>

          {/* Conteúdo: lista de publicações */}
          <div className="flex-1 p-5 pt-4">
            {selectedIds.size > 0 && (
              <div
                className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[10px] px-4 py-3"
                style={{
                  backgroundColor: '#FBFBFB',
                  border: '1px solid rgba(6, 20, 27, 0.08)',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                }}
              >
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#4A5568',
                  }}
                >
                  {selectedIds.size}{' '}
                  {selectedIds.size === 1
                    ? 'publicação selecionada'
                    : 'publicações selecionadas'}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="px-3 py-2 rounded-[10px] transition-opacity hover:opacity-80"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      backgroundColor: 'transparent',
                      color: '#718096',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    Limpar seleção
                  </button>
                  <button
                    type="button"
                    disabled={discardBulkMutation.isPending}
                    onClick={async () => {
                      if (!requirePermission('publications.treat', 'Descartar publicações')) {
                        return;
                      }
                      const count = selectedIds.size;
                      const ok = await confirmDialog({
                        title:
                          count === 1
                            ? 'Descartar publicação?'
                            : `Descartar ${count} publicações?`,
                        description:
                          'Elas sairão da lista de pendentes e poderão ser restauradas depois.',
                        confirmLabel: 'Descartar',
                        cancelLabel: 'Cancelar',
                        variant: 'warning',
                      });
                      if (!ok) return;
                      discardBulkMutation.mutate([...selectedIds]);
                    }}
                    className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: '#06141B',
                      color: '#F5F6F5',
                    }}
                  >
                    {discardBulkMutation.isPending
                      ? 'Descartando...'
                      : `Descartar selecionadas (${selectedIds.size})`}
                  </button>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="text-center py-12">
                <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#718096' }}>
                  Carregando publicações...
                </p>
              </div>
            ) : publicationsData && publicationsData.data.length > 0 ? (
              <div className="space-y-4">
                {publicationsData.data.map((publication) => (
                  <PublicationCard
                    key={publication.id}
                    publication={publication}
                    workspaceId={workspaceId}
                    pendingTaskCount={
                      treatDraftByPublicationId[publication.id]?.length ?? 0
                    }
                    selected={selectedIds.has(publication.id)}
                    onSelectChange={(next) => {
                      setSelectedIds((prev) => {
                        const copy = new Set(prev);
                        if (next) copy.add(publication.id);
                        else copy.delete(publication.id);
                        return copy;
                      });
                    }}
                    onTreat={() => {
                      if (!requirePermission('publications.treat', 'Tratar publicações')) return;
                      if (!requirePermission('tasks.create', 'Criar tarefas')) return;
                      setSelectedPublication(publication);
                      maybeMarkPublicationAsRead(publication, (id) =>
                        markAsReadMutation.mutate(id),
                      );
                    }}
                    onDiscard={async () => {
                      if (!requirePermission('publications.treat', 'Descartar publicações')) return;
                      const ok = await confirmDialog({
                        title: 'Descartar publicação?',
                        description:
                          'Ela sairá da lista de pendentes e poderá ser restaurada depois.',
                        confirmLabel: 'Descartar',
                        cancelLabel: 'Cancelar',
                        variant: 'warning',
                      });
                      if (!ok) return;
                      discardMutation.mutate(publication.id);
                    }}
                    isDiscarding={
                      discardMutation.isPending && discardMutation.variables === publication.id
                    }
                    onRestore={() => {
                      if (!requirePermission('publications.treat', 'Reabrir publicações')) return;
                      if (
                        window.confirm(
                          'Tem certeza que deseja restaurar esta publicação? Ela voltará para a lista de publicações não tratadas.',
                        )
                      ) {
                        restoreMutation.mutate(publication.id);
                      }
                    }}
                    isRestoring={
                      restoreMutation.isPending && restoreMutation.variables === publication.id
                    }
                    onConfirmSame={() => {
                      if (!requirePermission('publications.treat', 'Tratar publicações')) return;
                      duplicataMutation.mutate({ id: publication.id, mesma: true });
                    }}
                    onConfirmDifferent={() => {
                      if (!requirePermission('publications.treat', 'Tratar publicações')) return;
                      duplicataMutation.mutate({ id: publication.id, mesma: false });
                    }}
                    isResolvingDuplicata={
                      duplicataMutation.isPending &&
                      duplicataMutation.variables?.id === publication.id
                    }
                    onLinkedProcess={() => {
                      queryClient.invalidateQueries({
                        queryKey: ['publications', workspaceId],
                      });
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                  }}
                >
                  Nenhuma publicação encontrada
                </p>
              </div>
            )}

            {/* Paginação */}
            {publicationsData && publicationsData.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                <div
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                  }}
                >
                  Página {publicationsData.page + 1} de {publicationsData.totalPages} ({publicationsData.total} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      backgroundColor: page === 0 ? '#F9FAFB' : '#F9FAFB',
                      color: page === 0 ? '#A0AEC0' : '#2D3748',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    Anterior
                  </button>
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, publicationsData.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (publicationsData.totalPages <= 5) {
                      pageNum = i;
                    } else if (page < 3) {
                      pageNum = i;
                    } else if (page >= publicationsData.totalPages - 3) {
                      pageNum = publicationsData.totalPages - 5 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className="px-3 py-2 rounded-[10px] transition-colors hover:opacity-80"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          backgroundColor: page === pageNum ? '#485A6A' : '#F9FAFB',
                          color: page === pageNum ? '#FFFFFF' : '#2D3748',
                          border: page === pageNum ? 'none' : '1px solid #E2E8F0',
                        }}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(publicationsData.totalPages - 1, p + 1))}
                    disabled={page >= publicationsData.totalPages - 1}
                    className="px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      backgroundColor: page >= publicationsData.totalPages - 1 ? '#F9FAFB' : '#F9FAFB',
                      color: page >= publicationsData.totalPages - 1 ? '#A0AEC0' : '#2D3748',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Tratamento */}
      {selectedPublication && (
        <TreatPublicationModal
          key={selectedPublication.id}
          isOpen={!!selectedPublication}
          onClose={() => setSelectedPublication(null)}
          publication={selectedPublication}
          workspaceId={workspaceId}
          initialPendingRows={treatDraftByPublicationId[selectedPublication.id] ?? []}
          onPendingRowsChange={(rows) =>
            handleTreatPendingRowsChange(selectedPublication.id, rows)
          }
          onClearPublicationDraft={handleClearTreatPublicationDraft}
        />
      )}
    </div>
  );
}

